import React, { useState, useEffect, useRef } from 'react';
import './InputSearch.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faSearch,
    faTimes,
    faSuitcaseRolling,
    faFolder,
    faImages,
    faCalendarAlt,
    faImage,
    faEnvelope,
    faFileAlt,
    faBook,
    faUsers,
    faTicketAlt,
    faCog,
    faHome,
    faGlobe,
    faArrowRight,
    faMobileAlt,
    faCompass
} from '@fortawesome/free-solid-svg-icons';
import { Link, useNavigate } from 'react-router-dom';
import useAuthUser from '../../useAuthUser';
import baseURL from '../../url';

export default function InputSearch() {
    const { isAdmin } = useAuthUser();
    const navigate = useNavigate();
    const [searchTerm, setSearchTerm] = useState("");
    const [isOpen, setIsOpen] = useState(false);
    const [selectedIndex, setSelectedIndex] = useState(0);

    // Datos dinámicos del sistema
    const [productos, setProductos] = useState([]);
    const [categorias, setCategorias] = useState([]);
    const [eventos, setEventos] = useState([]);
    const [loadingData, setLoadingData] = useState(false);

    const containerRef = useRef(null);
    const inputRef = useRef(null);

    // Módulos base del sistema
    const modulosBase = [
        { title: 'Tours & Paquetes Turísticos', link: '/dashboard/productos', icon: faSuitcaseRolling, grupo: 'Módulos', desc: 'Gestiona viajes, precios y fotos' },
        { title: 'Categorías de Tours', link: '/dashboard/categorias', icon: faFolder, grupo: 'Módulos', desc: 'Organiza los destinos y tipos de viaje' },
        { title: 'Galería Fotográfica', link: '/dashboard/galeria', icon: faImages, grupo: 'Módulos', desc: 'Fotos y recuerdos de experiencias' },
        { title: 'Eventos & Festividades', link: '/dashboard/eventos', icon: faCalendarAlt, grupo: 'Módulos', desc: 'Calendario de actividades y fechas' },
        { title: 'Banners Publicitarios', link: '/dashboard/banners', icon: faImage, grupo: 'Módulos', desc: 'Sliders y promociones de portada' },
        { title: 'Mensajes de Contacto', link: '/dashboard/contacto', icon: faEnvelope, grupo: 'Módulos', desc: 'Consultas enviadas por clientes' },
        { title: 'Políticas de la Agencia', link: '/dashboard/politicas', icon: faFileAlt, grupo: 'Módulos', desc: 'Términos, privacidad y cancelaciones' },
        { title: 'Libro de Reclamaciones', link: '/dashboard/libro-reclamaciones', icon: faBook, grupo: 'Módulos', desc: 'Reclamos y quejas formales' },
        { title: 'Usuarios & Administradores', link: '/dashboard/usuarios', icon: faUsers, grupo: 'Módulos', desc: 'Gestión de accesos y personal', adminOnly: true },
        { title: 'Códigos Promocionales', link: '/dashboard/codigos', icon: faTicketAlt, grupo: 'Módulos', desc: 'Cupones de descuento para clientes' },
        { title: 'Configuración SEO, PWA & Marca', link: '/dashboard/configuracion', icon: faCog, grupo: 'Módulos', desc: 'Colores, logos, PWA y Google SEO', adminOnly: true },
        { title: 'Activar / Configurar PWA', link: '/dashboard/configuracion', icon: faMobileAlt, grupo: 'Módulos', desc: 'Aplicación web progresiva instalable', adminOnly: true }
    ].filter(item => !item.adminOnly || isAdmin);

    // Páginas públicas
    const paginasPublicas = [
        { title: 'Portada Principal de la Web', link: '/', icon: faHome, grupo: 'Web Pública', desc: 'Página de inicio para clientes' },
        { title: 'Quiénes Somos', link: '/quienes-somos', icon: faGlobe, grupo: 'Web Pública', desc: 'Información institucional de la agencia' },
        { title: 'Centros & Destinos', link: '/centros', icon: faCompass, grupo: 'Web Pública', desc: 'Sedes y oficinas de atención' },
        { title: 'Verificar Afiliado', link: '/verificar-afiliado', icon: faUsers, grupo: 'Web Pública', desc: 'Consulta de miembros y socios' },
        { title: 'Libro de Reclamaciones (Público)', link: '/libro-reclamaciones', icon: faBook, grupo: 'Web Pública', desc: 'Formulario oficial de reclamos' }
    ];

    // Cargar tours, categorías y eventos una sola vez para búsqueda instantánea en todo el sistema
    useEffect(() => {
        let isMounted = true;
        setLoadingData(true);

        Promise.all([
            fetch(`${baseURL}/productosGet.php`).then(r => r.json()).catch(() => []),
            fetch(`${baseURL}/categoriasGet.php`).then(r => r.json()).catch(() => ({ categorias: [] })),
            fetch(`${baseURL}/eventosGet.php`).then(r => r.json()).catch(() => ({ eventos: [] }))
        ]).then(([prodsData, catsData, eventsData]) => {
            if (!isMounted) return;
            if (Array.isArray(prodsData)) {
                setProductos(prodsData);
            }
            if (catsData && Array.isArray(catsData.categorias)) {
                setCategorias(catsData.categorias);
            }
            if (eventsData && Array.isArray(eventsData.eventos)) {
                setEventos(eventsData.eventos);
            }
        }).finally(() => {
            if (isMounted) setLoadingData(false);
        });

        return () => { isMounted = false; };
    }, []);

    // Atajo global Ctrl+K / Cmd+K y cerrar al hacer clic afuera
    useEffect(() => {
        const handleKeyDown = (e) => {
            if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
                e.preventDefault();
                inputRef.current?.focus();
                setIsOpen(true);
            } else if (e.key === 'Escape') {
                setIsOpen(false);
                inputRef.current?.blur();
            }
        };

        const handleClickOutside = (e) => {
            if (containerRef.current && !containerRef.current.contains(e.target)) {
                setIsOpen(false);
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            window.removeEventListener('keydown', handleKeyDown);
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const handleSearchChange = (e) => {
        const term = e.target.value;
        setSearchTerm(term);
        setIsOpen(true);
        setSelectedIndex(0);
    };

    const handleClear = () => {
        setSearchTerm("");
        inputRef.current?.focus();
    };

    // Búsqueda inteligente en todo el sistema
    const normalizar = (txt) => (txt || "").toString().toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");

    const termNorm = normalizar(searchTerm.trim());

    // 1. Filtrar módulos
    const modulosFiltrados = modulosBase.filter(m =>
        !termNorm || normalizar(m.title).includes(termNorm) || normalizar(m.desc).includes(termNorm)
    );

    // 2. Filtrar tours / productos
    const toursFiltrados = productos.filter(p =>
        termNorm && (
            normalizar(p.titulo).includes(termNorm) ||
            normalizar(p.categoria).includes(termNorm) ||
            normalizar(p.descripcion).includes(termNorm) ||
            (p.precio && p.precio.toString().includes(termNorm))
        )
    ).slice(0, 8); // Top 8 coincidencias

    // 3. Filtrar categorías
    const categoriasFiltradas = categorias.filter(c =>
        termNorm && normalizar(c.categoria).includes(termNorm)
    ).slice(0, 5);

    // 4. Filtrar eventos
    const eventosFiltrados = eventos.filter(ev =>
        termNorm && (
            normalizar(ev.nombre).includes(termNorm) ||
            normalizar(ev.descripcion).includes(termNorm)
        )
    ).slice(0, 4);

    // 5. Filtrar páginas públicas
    const paginasFiltradas = paginasPublicas.filter(pg =>
        termNorm && (
            normalizar(pg.title).includes(termNorm) ||
            normalizar(pg.desc).includes(termNorm)
        )
    );

    // Lista consolidada para navegación por teclado
    const todosResultados = [
        ...modulosFiltrados.map(m => ({ ...m, tipo: 'modulo' })),
        ...toursFiltrados.map(t => ({
            title: t.titulo,
            desc: `Precio: S/ ${t.precio || '0.00'} · Categoría: ${t.categoria || 'General'}`,
            link: `/dashboard/productos`,
            icon: faSuitcaseRolling,
            tipo: 'tour',
            badge: 'Tour'
        })),
        ...categoriasFiltradas.map(c => ({
            title: c.categoria,
            desc: `Categoría de viaje`,
            link: `/dashboard/categorias`,
            icon: faFolder,
            tipo: 'categoria',
            badge: 'Categoría'
        })),
        ...eventosFiltrados.map(ev => ({
            title: ev.nombre,
            desc: ev.descripcion || 'Evento de la agencia',
            link: `/dashboard/eventos`,
            icon: faCalendarAlt,
            tipo: 'evento',
            badge: 'Evento'
        })),
        ...paginasFiltradas.map(pg => ({ ...pg, tipo: 'publica', badge: 'Web' }))
    ];

    const totalCoincidencias = (termNorm ? todosResultados.length : modulosFiltrados.length);

    const handleKeyDownInput = (e) => {
        if (!isOpen) return;

        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setSelectedIndex(prev => (prev + 1 < todosResultados.length ? prev + 1 : 0));
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setSelectedIndex(prev => (prev > 0 ? prev - 1 : todosResultados.length - 1));
        } else if (e.key === 'Enter') {
            e.preventDefault();
            if (todosResultados[selectedIndex]) {
                navigate(todosResultados[selectedIndex].link);
                setIsOpen(false);
            }
        }
    };

    return (
        <div className="dashSearchContainer" ref={containerRef}>
            <div className={`dashSearchBox ${isOpen ? 'focused' : ''}`}>
                <div className="dashSearchLensWrap">
                    <FontAwesomeIcon icon={faSearch} className="dashSearchLensIcon" />
                </div>
                <input
                    ref={inputRef}
                    type="text"
                    placeholder="Buscar en todo el sistema (tours, módulos, eventos)..."
                    value={searchTerm}
                    onChange={handleSearchChange}
                    onFocus={() => setIsOpen(true)}
                    onKeyDown={handleKeyDownInput}
                    className="dashSearchInput"
                />
                {searchTerm ? (
                    <button
                        type="button"
                        onClick={handleClear}
                        className="dashSearchClearBtn"
                        title="Limpiar búsqueda"
                    >
                        <FontAwesomeIcon icon={faTimes} />
                    </button>
                ) : (
                    <span className="dashSearchKbdHint">Ctrl K</span>
                )}
            </div>

            {isOpen && (
                <div className="dashSearchResultsModal">
                    {/* Encabezado con estado */}
                    <div className="dashSearchModalHeader">
                        <span className="dashSearchCounter">
                            {termNorm ? `${totalCoincidencias} resultado(s) para "${searchTerm}"` : 'Accesos directos y módulos del sistema'}
                        </span>
                        {loadingData && <span className="dashSearchLoading">Sincronizando...</span>}
                    </div>

                    <div className="dashSearchScrollArea">
                        {/* 1. Módulos del Sistema */}
                        {modulosFiltrados.length > 0 && (
                            <div className="dashSearchGroup">
                                <div className="dashSearchGroupTitle">
                                    <span>MÓDULOS DEL PANEL</span>
                                    <span className="dashSearchGroupBadge">{modulosFiltrados.length}</span>
                                </div>
                                {modulosFiltrados.map((item, idx) => (
                                    <Link
                                        key={`mod-${idx}`}
                                        to={item.link}
                                        onClick={() => setIsOpen(false)}
                                        className={`dashSearchResultItem ${selectedIndex === idx ? 'selected' : ''}`}
                                    >
                                        <div className="dashItemIconWrap modulo">
                                            <FontAwesomeIcon icon={item.icon} />
                                        </div>
                                        <div className="dashItemTexts">
                                            <span className="dashItemTitle">{item.title}</span>
                                            <span className="dashItemDesc">{item.desc}</span>
                                        </div>
                                        <FontAwesomeIcon icon={faArrowRight} className="dashItemArrow" />
                                    </Link>
                                ))}
                            </div>
                        )}

                        {/* 2. Tours y Paquetes Turísticos reales */}
                        {toursFiltrados.length > 0 && (
                            <div className="dashSearchGroup">
                                <div className="dashSearchGroupTitle">
                                    <span>TOURS & PAQUETES TURÍSTICOS</span>
                                    <span className="dashSearchGroupBadge green">{toursFiltrados.length}</span>
                                </div>
                                {toursFiltrados.map((tour, idx) => (
                                    <Link
                                        key={`tour-${tour.idProducto || idx}`}
                                        to={`/dashboard/productos`}
                                        onClick={() => setIsOpen(false)}
                                        className="dashSearchResultItem"
                                    >
                                        <div className="dashItemIconWrap tour">
                                            <FontAwesomeIcon icon={faSuitcaseRolling} />
                                        </div>
                                        <div className="dashItemTexts">
                                            <div className="dashItemTitleFlex">
                                                <span className="dashItemTitle">{tour.titulo}</span>
                                                {tour.precio && (
                                                    <span className="dashItemPriceTag">S/ {parseFloat(tour.precio).toFixed(2)}</span>
                                                )}
                                            </div>
                                            <span className="dashItemDesc">
                                                {tour.categoria ? `Categoría: ${tour.categoria}` : ''}
                                                {tour.descripcion ? ` · ${tour.descripcion.slice(0, 50)}...` : ''}
                                            </span>
                                        </div>
                                        <span className="dashItemCategoryBadge">Tour</span>
                                    </Link>
                                ))}
                            </div>
                        )}

                        {/* 3. Categorías reales */}
                        {categoriasFiltradas.length > 0 && (
                            <div className="dashSearchGroup">
                                <div className="dashSearchGroupTitle">
                                    <span>CATEGORÍAS</span>
                                    <span className="dashSearchGroupBadge">{categoriasFiltradas.length}</span>
                                </div>
                                {categoriasFiltradas.map((cat, idx) => (
                                    <Link
                                        key={`cat-${cat.idCategoria || idx}`}
                                        to="/dashboard/categorias"
                                        onClick={() => setIsOpen(false)}
                                        className="dashSearchResultItem"
                                    >
                                        <div className="dashItemIconWrap categoria">
                                            <FontAwesomeIcon icon={faFolder} />
                                        </div>
                                        <div className="dashItemTexts">
                                            <span className="dashItemTitle">{cat.categoria}</span>
                                            <span className="dashItemDesc">Ver tours en esta categoría</span>
                                        </div>
                                        <span className="dashItemCategoryBadge">Categoría</span>
                                    </Link>
                                ))}
                            </div>
                        )}

                        {/* 4. Eventos reales */}
                        {eventosFiltrados.length > 0 && (
                            <div className="dashSearchGroup">
                                <div className="dashSearchGroupTitle">
                                    <span>EVENTOS & FESTIVIDADES</span>
                                    <span className="dashSearchGroupBadge">{eventosFiltrados.length}</span>
                                </div>
                                {eventosFiltrados.map((ev, idx) => (
                                    <Link
                                        key={`ev-${ev.idEvento || idx}`}
                                        to="/dashboard/eventos"
                                        onClick={() => setIsOpen(false)}
                                        className="dashSearchResultItem"
                                    >
                                        <div className="dashItemIconWrap evento">
                                            <FontAwesomeIcon icon={faCalendarAlt} />
                                        </div>
                                        <div className="dashItemTexts">
                                            <span className="dashItemTitle">{ev.nombre}</span>
                                            <span className="dashItemDesc">{ev.descripcion || 'Ver fecha y detalles'}</span>
                                        </div>
                                        <span className="dashItemCategoryBadge">Evento</span>
                                    </Link>
                                ))}
                            </div>
                        )}

                        {/* 5. Páginas públicas */}
                        {paginasFiltradas.length > 0 && (
                            <div className="dashSearchGroup">
                                <div className="dashSearchGroupTitle">
                                    <span>PÁGINAS PÚBLICAS (WEB)</span>
                                    <span className="dashSearchGroupBadge">{paginasFiltradas.length}</span>
                                </div>
                                {paginasFiltradas.map((pg, idx) => (
                                    <Link
                                        key={`pg-${idx}`}
                                        to={pg.link}
                                        onClick={() => setIsOpen(false)}
                                        className="dashSearchResultItem"
                                    >
                                        <div className="dashItemIconWrap web">
                                            <FontAwesomeIcon icon={pg.icon} />
                                        </div>
                                        <div className="dashItemTexts">
                                            <span className="dashItemTitle">{pg.title}</span>
                                            <span className="dashItemDesc">{pg.desc}</span>
                                        </div>
                                        <span className="dashItemCategoryBadge">Web</span>
                                    </Link>
                                ))}
                            </div>
                        )}

                        {/* Mensaje de no resultados */}
                        {totalCoincidencias === 0 && (
                            <div className="dashSearchNoResults">
                                <div className="dashNoResultsIcon">🔍</div>
                                <h6>No se encontraron resultados</h6>
                                <p>No hay coincidencias para "<strong>{searchTerm}</strong>". Prueba buscando con palabras como "tour", "evento", "contacto" o "configuración".</p>
                            </div>
                        )}
                    </div>

                    {/* Footer de navegación rápida */}
                    <div className="dashSearchModalFooter">
                        <span className="dashFooterShortcut"><span>↑</span> <span>↓</span> para navegar</span>
                        <span className="dashFooterShortcut"><span>Enter</span> para abrir</span>
                        <span className="dashFooterShortcut"><span>Esc</span> para cerrar</span>
                    </div>
                </div>
            )}
        </div>
    );
}
